import { getAnalytics } from 'firebase/analytics';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getMessaging, type Messaging } from 'firebase/messaging';
import { getPerformance } from 'firebase/performance';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { resolveEmulatorHosts } from '../../shared/emulatorHosts';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

const missingEnvVars = requiredEnvVars.filter(envVar => !import.meta.env[envVar]);

if (missingEnvVars.length > 0) {
  throw new Error(`Missing required Firebase environment variables: ${missingEnvVars.join(', ')}`);
}

// Initialize Firebase securely
export const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

export const auth = getAuth(app);
export const functions = getFunctions(app, 'europe-west1');

// Use getFirestore securely with the custom database ID from environment
export const databaseId = import.meta.env.VITE_FIREBASE_DATABASE_ID?.trim() || '(default)';
export const db = databaseId === '(default)' ? getFirestore(app) : getFirestore(app, databaseId);

export const storage = getStorage(app);
export const analytics = getAnalytics(app);
export const performance = getPerformance(app);

export const messaging: Messaging | null = (() => {
  try {
    return getMessaging(app);
  } catch (err) {
    console.warn('Firebase Messaging is not supported in this environment:', err);
    return null;
  }
})();

// Connect to emulators if enabled
const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

if (useEmulators) {
  if (typeof window !== 'undefined') {
    // Only connect to emulators in browser environment
    const emulatorHosts = resolveEmulatorHosts(import.meta.env);
    connectAuthEmulator(auth, emulatorHosts.auth.url);
    connectFirestoreEmulator(db, emulatorHosts.firestore.host, emulatorHosts.firestore.port);
    connectFunctionsEmulator(functions, emulatorHosts.functions.host, emulatorHosts.functions.port);
    connectStorageEmulator(storage, emulatorHosts.storage.host, emulatorHosts.storage.port);
    console.log('🔥 Firebase Emulators connected');
  }
}
